import React from 'react';
import './Manage_monthly_gift.css';
import { Helmet } from 'react-helmet';
import GGApi from '../../../utils/GGApi';
import GG_loader from '../../general_components/GG_loader/GG_loader';
import Overlay_popup from '../../general_components/Overlay_popup/Overlay_popup';
import GG_confetti from '../../general_components/GG_confetti/GG_confetti';
import {
  logMonthlyManagePageViewed,
  logMonthlyPlanCanceled,
} from '../../../utils/monthlyGiftAnalytics';

// Format a date/datetime string as "Month D, YYYY"
function formatDate(raw) {
  if (!raw) return '—';
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return String(raw).slice(0, 10);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch (e) {
    return String(raw).slice(0, 10);
  }
}

// Human-readable plan status label
function statusLabel(status) {
  switch (status) {
    case 'active': return 'Active';
    case 'payment_failed_active': return 'Payment issue';
    case 'completed': return 'Completed';
    case 'canceled': return 'Canceled';
    default: return status || '—';
  }
}

class Manage_monthly_gift extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // page state
      loading: true,
      invalid: false,
      error: null,
      // plan data
      details: null,
      // cancel flow
      cancel_overlay_open: false,
      canceling: false,   // POST in flight
      canceled: false,    // successfully canceled
    };
    this.getToken = this.getToken.bind(this);
    this.handleCancelClick = this.handleCancelClick.bind(this);
    this.handleConfirmCancel = this.handleConfirmCancel.bind(this);
    this.handleKeepClick = this.handleKeepClick.bind(this);
    this.handleCloseCancelOverlay = this.handleCloseCancelOverlay.bind(this);
  }

  getToken() {
    const search = this.props.location && this.props.location.search ? this.props.location.search : '';
    const params = new URLSearchParams(search);
    return params.get('t') || '';
  }

  componentDidMount() {
    const token = this.getToken();
    if (!token) {
      this.setState({ loading: false, invalid: true });
      return;
    }
    GGApi.get_monthly_gift_manage(token)
      .then((data) => {
        if (data.invalid || (!data.plan_id && !data.status)) {
          this.setState({ loading: false, invalid: true, error: data.message || null });
          return;
        }
        // Already canceled or completed — show read-only state
        if (data.status === 'canceled') {
          logMonthlyManagePageViewed({ plan_id: data.plan_id, status: 'canceled' });
          this.setState({ loading: false, details: data, canceled: true });
          return;
        }
        logMonthlyManagePageViewed({ plan_id: data.plan_id, status: data.status });
        this.setState({ loading: false, details: data });
      })
      .catch(() => this.setState({ loading: false, invalid: true, error: 'Something went wrong.' }));
  }

  handleCancelClick() {
    this.setState({ cancel_overlay_open: true, error: null });
  }

  handleCloseCancelOverlay() {
    if (this.state.canceling) return;
    this.setState({ cancel_overlay_open: false, error: null });
  }

  handleKeepClick() {
    this.handleCloseCancelOverlay();
  }

  handleConfirmCancel() {
    const token = this.getToken();
    if (!token || this.state.canceling) return;
    this.setState({ canceling: true });
    GGApi.cancel_monthly_gift(token)
      .then((data) => {
        if (data.success) {
          if (!data.already_canceled && this.state.details) {
            logMonthlyPlanCanceled({
              plan_id: this.state.details.plan_id,
              months_completed: this.state.details.months_completed,
              total_months: this.state.details.total_months,
            });
          }
          this.setState({ canceling: false, cancel_overlay_open: false, canceled: true });
        } else {
          this.setState({ canceling: false, error: data.message || 'Could not cancel. Please try again.' });
        }
      })
      .catch(() => this.setState({ canceling: false, error: 'Something went wrong. Please try again.' }));
  }

  renderCancelOverlay(details) {
    const { cancel_overlay_open, canceling, error } = this.state;
    if (!details) return null;

    return (
      <Overlay_popup
        open={cancel_overlay_open}
        closer={true}
        handleClose={this.handleCloseCancelOverlay}
        overlay_title="Cancel monthly gift?"
        body_1={`Are you sure? Canceling will stop all future monthly gifts for ${details.account_name}. Gifts already sent will not be refunded.`}
        body_2={error || ''}
        button_1={{
          text: canceling ? 'Canceling…' : 'Yes, cancel my monthly gift',
          handler: this.handleConfirmCancel,
          inactive_button: canceling,
          disabled: canceling,
        }}
        joker={
          <div className="manage_mg_overlay_joker">
            <p
              className={`manage_mg_cancel_link secondary_dark bold${canceling ? ' manage_mg_cancel_link_disabled' : ''}`}
              onClick={canceling ? undefined : this.handleKeepClick}
            >
              Never mind, keep my monthly gift
            </p>
          </div>
        }
      />
    );
  }

  render() {
    const { loading, invalid, error, details, canceling, canceled } = this.state;
    // Token-gated monthly-gift management — email deep link only; excluded from sitemap.
    const noindexHelmet = (
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
    );

    // --- Loading ---
    if (loading) {
      return (
        <>
          {noindexHelmet}
        <div className="manage_mg_wrapper">
          <main className="manage_mg_main">
            <GG_loader />
          </main>
        </div>
        </>
      );
    }

    // --- Invalid / expired token ---
    if (invalid) {
      return (
        <>
          {noindexHelmet}
        <div className="manage_mg_wrapper">
          <main className="manage_mg_main">
            <div className="manage_mg_card">
              <h1 className="manage_mg_title">Invalid or expired link</h1>
              <p className="manage_mg_text">
                {error
                  ? error
                  : 'This link is invalid or has expired. If you have questions, please email our support team at support@greatestgiftapp.com.'}
              </p>
            </div>
          </main>
        </div>
        </>
      );
    }

    // --- Successfully canceled (fresh cancel or already was canceled) ---
    if (canceled) {
      const alreadyCanceled = details && details.status === 'canceled';
      return (
        <>
          {noindexHelmet}
        <div className="manage_mg_wrapper">
          {!alreadyCanceled && <GG_confetti />}
          <main className="manage_mg_main">
            <div className="manage_mg_card">
              <h1 className="manage_mg_title">{alreadyCanceled ? 'Already canceled' : 'Monthly gift canceled'}</h1>
              <p className="manage_mg_text">
                {alreadyCanceled
                  ? `This monthly gift for ${details.account_name || 'your recipient'} has already been canceled. No future charges will be made.`
                  : `Your monthly gift for ${details && details.account_name ? details.account_name : 'your recipient'} has been canceled. No future charges will be made.`}
              </p>
            </div>
            <div className="manage_mg_card">
              <p className="manage_mg_text">
                If you have any questions, please email our support team at support@greatestgiftapp.com.
              </p>
            </div>
          </main>
        </div>
        </>
      );
    }

    // --- Plan completed (all months done — no cancel available) ---
    if (details && details.status === 'completed') {
      return (
        <>
          {noindexHelmet}
        <div className="manage_mg_wrapper">
          <main className="manage_mg_main">
            <div className="manage_mg_card">
              <h1 className="manage_mg_title">Monthly gift complete</h1>
              <p className="manage_mg_text">
                Your {details.total_months}-month gift for <strong>{details.account_name}</strong> is complete. All {details.months_completed} gifts have been sent — thank you for your generosity!
              </p>
            </div>
            <div className="manage_mg_card">
              <p className="manage_mg_text">
                If you have any questions, please email our support team at support@greatestgiftapp.com.
              </p>
            </div>
          </main>
        </div>
        </>
      );
    }

    if (!details) return null;

    const isPastDue = details.status === 'payment_failed_active';
    const amountDisplay = `$${Number(details.gift_amount_dollars || 0).toFixed(0)}/mo`;

    // --- Main manage view ---
    return (
      <>
        {noindexHelmet}
      <div className="manage_mg_wrapper">
        {this.renderCancelOverlay(details)}
        <main className="manage_mg_main">

          {/* Plan summary card */}
          <div className="manage_mg_card manage_mg_card_primary">
            <h1 className="manage_mg_title">Manage monthly gift</h1>
            <p className="manage_mg_text">
              You are sending a monthly gift of <strong>{amountDisplay}</strong> for{' '}
              <strong>{details.account_name}</strong>.
            </p>

            <div className="manage_mg_details">
              <p className="small_text">
                <span className="manage_mg_label">Gift amount:</span>
                <span className="manage_mg_value">{amountDisplay}</span>
              </p>
              <p className="small_text">
                <span className="manage_mg_label">Progress:</span>
                <span className="manage_mg_value">
                  {details.months_completed} of {details.total_months} month{details.total_months !== 1 ? 's' : ''}
                </span>
              </p>
              {details.gifts_remaining > 0 && (
                <p className="small_text">
                  <span className="manage_mg_label">Gifts remaining:</span>
                  <span className="manage_mg_value">{details.gifts_remaining}</span>
                </p>
              )}
              {details.next_charge_at && !isPastDue && (
                <p className="small_text">
                  <span className="manage_mg_label">Next charge:</span>
                  <span className="manage_mg_value">{formatDate(details.next_charge_at)}</span>
                </p>
              )}
              <p className="small_text">
                <span className="manage_mg_label">Status:</span>
                <span className={isPastDue ? 'manage_mg_status_failed' : 'manage_mg_value'}>
                  {statusLabel(details.status)}
                </span>
              </p>
            </div>

            {isPastDue && (
              <p className="manage_mg_text" style={{ marginTop: 12 }}>
                <span className="material-icons-outlined inline_icon" style={{ fontSize: '1rem', verticalAlign: 'middle', marginRight: 4 }}>info</span>
                Your last payment didn&apos;t go through. We&apos;re retrying — if payments continue to fail, you can cancel below to prevent future attempts.
              </p>
            )}

            <div className="manage_mg_actions">
              <p
                className="manage_mg_cancel_link secondary_dark bold"
                onClick={this.handleCancelClick}
              >
                Cancel Monthly Gift
              </p>
            </div>
          </div>

          {/* Informational card */}
          <div className="manage_mg_card">
            <h2 className="manage_mg_subtitle">About Monthly Gifts</h2>
            <p className="manage_mg_text">
              Monthly gifts are a great way to invest consistently in a child&apos;s future. 
            </p>
            <p className="manage_mg_text">
              If you no longer want to continue sending gifts, you can cancel the monthly gift.
              If you choose to cancel, only future gifts are stopped. Any gifts already sent will not be refunded.
            </p>
            <p className="manage_mg_text">
              Have questions? Email us at support@greatestgiftapp.com.
            </p>
          </div>

        </main>
      </div>
      </>
    );
  }
}

export default Manage_monthly_gift;
